// Mock data for Exquisite Concierge Medicine

export const practiceInfo = {
  name: "Exquisite Concierge Medicine",
  tagline: "Personalized Healthcare, Elevated",
  description: "Experience medicine as it should be—unhurried, personalized, and focused entirely on you. At Exquisite Concierge Medicine, we limit our practice to ensure each patient receives the exceptional care they deserve.",
  phone: "(405) 613-1044",
  email: "robertconcierge@exquisitemedspaok.com",
  address: "5713 NW 132nd Street, Oklahoma City, OK 73142",
  hours: "By Appointment",
  logo: "https://customer-assets.emergentagent.com/job_63cb8d0a-4a20-4db2-86b9-43b78e271d9a/artifacts/zpnbk0ek_e.png"
};

export const services = [
  {
    id: 1,
    title: "Comprehensive Annual Wellness Exams",
    description: "In-depth health assessments lasting 60-90 minutes, including advanced diagnostics, lifestyle evaluation, and personalized wellness planning.",
    icon: "Stethoscope"
  },
  {
    id: 2,
    title: "Preventative Care & Screening",
    description: "Proactive health management with advanced screening protocols, genetic testing, and early detection strategies tailored to your risk profile.",
    icon: "Shield"
  },
  {
    id: 3,
    title: "Hormone Replacement Therapy",
    description: "Bioidentical hormone optimization for both men and women, addressing fatigue, weight management, mood, and vitality with precision medicine.",
    icon: "Activity"
  },
  {
    id: 4,
    title: "Chronic Disease Management",
    description: "Comprehensive management of diabetes, hypertension, thyroid disorders, and other chronic conditions with a focus on lifestyle integration.",
    icon: "Heart"
  },
  {
    id: 5,
    title: "Same-Day & Next-Day Appointments",
    description: "When you need care, you receive it promptly. No more waiting weeks for appointments or hours in waiting rooms.",
    icon: "Calendar"
  },
  {
    id: 6,
    title: "24/7 Provider Access",
    description: "Direct access to your provider via phone, text, or secure messaging—day or night, weekends and holidays included.",
    icon: "Phone"
  },
  {
    id: 7,
    title: "Care Coordination",
    description: "Seamless coordination with specialists, hospitals, and healthcare facilities. We advocate for you throughout your healthcare journey.",
    icon: "Users"
  },
  {
    id: 8,
    title: "Executive Health Programs",
    description: "Comprehensive health optimization programs designed for busy professionals who demand excellence in their healthcare.",
    icon: "Briefcase"
  }
];

export const membershipTiers = [
  {
    id: 1,
    name: "Virtual - Basic",
    price: "75",
    period: "per month",
    description: "24/7 telehealth access for individuals seeking convenient care for minor acute illnesses.",
    features: [
      "Unlimited telehealth visits",
      "Phone or video consultations",
      "24/7 provider availability",
      "Minor acute illness treatment",
      "Prescription management",
      "Secure messaging",
      "No appointment needed",
      "A care model aimed at lowering preventable urgent care and emergency department utilization"
    ],
    popular: false
  },
  {
    id: 2,
    name: "Essential",
    price: "150",
    period: "per month",
    description: "Comprehensive concierge care for individuals seeking personalized attention.",
    features: [
      "Everything in Virtual - Basic, plus:",
      "Unlimited office visits",
      "Same-day appointments",
      "Extended visit times (30-60 min)",
      "Direct provider phone access",
      "Annual comprehensive wellness exam",
      "Care coordination",
      "Secure messaging portal",
      "Imaging studies available at Touchstone Imaging"
    ],
    popular: true
  },
  {
    id: 3,
    name: "Premier",
    price: "300",
    period: "per month",
    description: "Enhanced care with advanced diagnostics and 24/7 access for those who expect more.",
    features: [
      "Everything in Essential, plus:",
      "24/7 provider access",
      "Home visits when needed",
      "Basic labs included",
      "Advanced diagnostic panels at discounted rate",
      "Discounted genetic health screening",
      "Nutritional counseling",
      "Priority specialist referrals",
      "State-of-the-art DermaSensor® for early detection support for skin cancer",
      "Heartsmart® EKG (annually)",
      "Imaging studies available at Touchstone Imaging"
    ],
    popular: false
  },
  {
    id: 4,
    name: "Executive",
    price: "500",
    period: "per month",
    description: "The ultimate in personalized medicine for discerning individuals and families.",
    features: [
      "Everything in Premier, plus:",
      "Includes spouse",
      "Executive health assessment",
      "Anti-aging medicine protocols",
      "IV vitamin therapy sessions at discounted rate",
      "Complimentary aesthetic consultations",
      "Imaging studies available at Touchstone Imaging"
    ],
    popular: false
  }
];

export const enrollmentSteps = [
  {
    step: 1,
    title: "Initial Consultation",
    description: "Schedule a complimentary 30-minute consultation to discuss your healthcare needs and determine if our practice is the right fit for you."
  },
  {
    step: 2,
    title: "Membership Selection",
    description: "Choose the membership tier that aligns with your health goals and lifestyle. Our team will guide you through the options."
  },
  {
    step: 3,
    title: "Complete Enrollment",
    description: "Submit your new patient forms, medical history, and complete the enrollment process. We handle the details."
  },
  {
    step: 4,
    title: "Welcome Visit",
    description: "Your journey begins with a comprehensive 90-minute welcome visit with your provider to establish your personalized care plan."
  }
];

export const blogPosts = [
  {
    id: 1,
    title: "The Benefits of Concierge Medicine: Healthcare Reimagined",
    excerpt: "Discover how concierge medicine transforms the patient experience with unhurried appointments, direct provider access, and truly personalized care.",
    category: "Concierge Care",
    date: "August 12, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800"
  },
  {
    id: 2,
    title: "Preventative Care vs. Reactive Medicine: A Paradigm Shift",
    excerpt: "Why waiting until you're sick is costing you more than money. Learn how proactive health management can add years to your life.",
    category: "Preventative Health",
    date: "August 5, 2025",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800"
  },
  {
    id: 3,
    title: "Understanding Hormone Replacement Therapy: Facts & Benefits",
    excerpt: "Bioidentical hormones explained—how optimizing your hormones can restore energy, improve mood, and enhance your quality of life.",
    category: "Hormone Health",
    date: "July 28, 2025",
    readTime: "10 min read",
    image: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800"
  },
  {
    id: 4,
    title: "Managing Diabetes Beyond Medicine: A Holistic Approach",
    excerpt: "Medications are just one piece of the puzzle. Explore lifestyle interventions, nutrition strategies, and the latest in diabetes care.",
    category: "Chronic Care",
    date: "July 20, 2025",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=800"
  },
  {
    id: 5,
    title: "Hypertension: Lifestyle Plus Medical Management",
    excerpt: "High blood pressure doesn't have to control your life. Learn integrative approaches that combine the best of medicine and lifestyle.",
    category: "Heart Health",
    date: "July 12, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1628348070889-cb656235b4eb?w=800"
  },
  {
    id: 6,
    title: "Why Annual Wellness Visits Matter More Than You Think",
    excerpt: "Your yearly check-up is more than routine—it's your best defense against serious illness. Here's what a comprehensive exam should include.",
    category: "Wellness",
    date: "July 5, 2025",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800"
  }
];

export const faqs = [
  {
    question: "How is concierge medicine different from traditional healthcare?",
    answer: "Concierge medicine limits the number of patients a provider sees, allowing for longer appointments, same-day access, and a deeper doctor-patient relationship. Instead of 2,000+ patients, we care for approximately 300, ensuring you receive the attention you deserve."
  },
  {
    question: "Do I still need health insurance?",
    answer: "Yes, we recommend maintaining health insurance for hospitalizations, specialist care, and major medical expenses. Your membership covers primary care services, which often reduces your overall healthcare costs through preventative care and early intervention."
  },
  {
    question: "What happens if I need to see a specialist?",
    answer: "We coordinate all specialist referrals and ensure you see the best providers for your needs. Our established relationships with top specialists often mean shorter wait times and better communication between your care team."
  },
  {
    question: "Can I reach the doctor after hours?",
    answer: "Yes, Premier and Executive members have 24/7 direct access to their provider. Essential members have access during extended office hours and can always reach our on-call service for urgent matters."
  },
  {
    question: "Is there a contract or commitment period?",
    answer: "We ask for an initial 3-month commitment to allow time for your comprehensive onboarding. After that, membership continues month-to-month and can be cancelled with 30 days notice."
  }
];

export const testimonials = [
  {
    id: 1,
    quote: "For the first time I feel heard and understood. My provider knows me as a person and not just a chart and goes above and beyond my expectations.",
    author: "Kerison P.",
    title: "Essential Member since 2025"
  },
  {
    id: 2,
    quote: "He truly cares about his patients and gives them a voice every time you see him. You do not get that feeling with many providers anymore. With Robert you are more than just a number, you know you have been heard and cared for. He gives you that old school small town Dr. feel. It's not just a feeling, it is reality. 11/10 rating—he is a keeper.",
    author: "Charlie L.",
    title: "Essential Member since 2025"
  },
  {
    id: 3,
    quote: "Concierge medicine is amazing. The service is truly exquisite and Robert truly listens and addresses each and every one of my problems with compassion. Getting a same-day appointment is amazing and so helpful with my busy life.",
    author: "Chad B.",
    title: "Premier Member since 2025"
  }
];

export const patientForms = [
  {
    id: 1,
    name: "New Patient Registration Form",
    description: "Complete patient information and contact details",
    type: "PDF"
  },
  {
    id: 2,
    name: "Medical History Questionnaire",
    description: "Comprehensive health history and current medications",
    type: "PDF"
  },
  {
    id: 3,
    name: "HIPAA Privacy Authorization",
    description: "Privacy practices acknowledgment and consent",
    type: "PDF"
  },
  {
    id: 4,
    name: "Insurance Information Form",
    description: "Insurance details for coordination of benefits",
    type: "PDF"
  },
  {
    id: 5,
    name: "Membership Agreement",
    description: "Terms of membership and service agreement",
    type: "PDF"
  }
];
